
import type { PropType } from 'vue';

import _debounce from 'lodash/debounce';

import axios from 'axios';

import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
} from 'vue';

import PrimeVueMultiSelect from 'primevue/multiselect';

import PrimeVueInputText from 'primevue/inputtext';

import { AUTOCOMPLETE_ENTRIES_LIMIT } from '@/libs/consts';

export default defineComponent({
  components: {
    PrimeVueMultiSelect,
    PrimeVueInputText,
  },
  props: {
    modelValue: {
      type: Array as PropType<number[]>,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    apiUrl: {
      type: String,
      required: true,
    },
    apiParams: {
      type: Object,
      default: () => ({}),
    },
    optionLabel: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const options = ref<{ id: number }[]>([]);

    const sortedOptions = computed(() => [...options.value].sort((a, b) => Number(props.modelValue.includes(b.id)) - Number(props.modelValue.includes(a.id))));

    const optionsMap = computed(() => options.value.reduce((acc, option) => {
      acc[option.id] = option[props.optionLabel as keyof typeof option];

      return acc;
    }, {} as Record<string, number>));

    const keyword = ref();

    const loading = ref(false);

    const getOptions = async (): Promise<void> => {
      loading.value = true;

      try {
        const response = await axios.get(
          props.apiUrl,
          {
            params: {
              'filter[id]': props.modelValue.length && !options.value.length ? props.modelValue.join(',') : undefined,
              'filter[keyword]': keyword.value,
              sort: '-id',
              per_page: AUTOCOMPLETE_ENTRIES_LIMIT,
              ...props.apiParams,
            },
          },
        );

        const mergedOptions = [
          ...options.value.filter((option) => props.modelValue.includes(option.id)),
          ...response.data.data,
        ];

        options.value = [...new Map(mergedOptions.map((option) => [option.id, option])).values()];
      } catch (err) {
        console.error(err);
      }

      loading.value = false;
    };

    watch(keyword, _debounce(() => getOptions(), 1000));

    watch(() => props.apiParams, () => getOptions());

    onMounted(() => getOptions());

    return {
      sortedOptions,
      optionsMap,
      keyword,
      loading,
    };
  },
});
